import axios from 'axios'
import { isEmpty, isMatch, isNil, isString, omit, pickBy } from 'lodash'

import { Company } from '../models/companyModel'
import { UpdateAttachments } from '../models/invoiceModels'
import { Attachment, AttachmentGateway, AttachmentWithIdsGateway, UploadFileGateway } from '../models/uploadModels'
import { resolveApiUrl } from '../utils/requestsUtils'
import { RequestResponse, requestService } from './requestServices'

export const uploadFileService = async (
    files: (File | Blob)[] | undefined,
    companyId: Company['id']
): Promise<UploadFileGateway[] | undefined> => {
    const filesResponse: (UploadFileGateway | undefined)[] = []
    try {
        if (isNil(files) || isEmpty(files)) return undefined
        const url = resolveApiUrl('upload.create', { variables: { companyId } })

        for (const file of files) {
            const formattedFile = new FormData()
            formattedFile.append('file', file)
            const response = await requestService<UploadFileGateway[]>(url, { method: 'POST', data: formattedFile })
            filesResponse.push(response?.body?.[0])
        }
        return filesResponse.filter((file): file is UploadFileGateway => !isNil(file))
    } catch (error) {
        console.error('uploadFileService' + error)
        return filesResponse.filter((file): file is UploadFileGateway => !isNil(file))
    }
}

export const downloadAttachments = async (
    attachments: string[] | AttachmentGateway[] | AttachmentWithIdsGateway[] | null | undefined
): Promise<Attachment[] | undefined> => {
    try {
        if (isEmpty(attachments) || !attachments) {
            return []
        }

        return await Promise.all(
            attachments.map(async (attachment) => {
                const url = isString(attachment) ? attachment : (attachment.url ?? '')

                const response = await axios(url, { responseType: 'blob' })

                const contentDisposition = response.headers['content-disposition']
                let filename = ''
                if (contentDisposition) {
                    const matches = contentDisposition.match(/filename="([^"]+)"/)
                    filename = matches ? matches[1] : ''
                }

                filename = decodeURIComponent(filename).replace(/%22/g, '')

                const contentType = response.headers['content-type']
                const file = new File([response.data], filename, {
                    type: typeof contentType === 'string' ? contentType : undefined
                })

                if (isString(attachment)) {
                    return { url: attachment, file }
                }

                if ('id' in attachment) {
                    return {
                        ...attachment,
                        file,
                        url: attachment.url ?? ''
                    }
                }

                return { ...attachment, file }
            })
        )
    } catch (error) {
        console.error('downloadAttachments: ' + error)
        throw error
    }
}

export type State = 'update' | 'delete'

function removeAttachmentFromList(attachments: Attachment[], attachment: Attachment): Attachment[] {
    const toRemoveCriteria = pickBy(
        {
            file: attachment.file,
            url: attachment.url,
            type: attachment.type
        },
        (value) => !isNil(value)
    )
    return attachments.filter((attachment) => !isMatch(attachment, toRemoveCriteria))
}

export const updateAttachmentsService = async <T extends { id: string; attachments?: Attachment[] }>(
    key: 'loan' | 'invoice' | 'receivables',
    companyId: string,
    payload: UpdateAttachments<T>,
    state: State,
    mapResponse?: (data: T) => Promise<T>
): Promise<RequestResponse<T> | undefined> => {
    const { id, attachments, attachment } = payload
    let formattedAttachments: AttachmentGateway[] = []

    switch (state) {
        case 'delete': {
            formattedAttachments = removeAttachmentFromList(attachments ?? [], attachment)
            break
        }

        case 'update': {
            const existingAttachments = attachments?.map((attachment) => omit(attachment, ['file'])) ?? []

            let newAttachments: AttachmentGateway[] = []
            if (payload.attachment?.file) {
                const uploadResult = await uploadFileService([payload.attachment.file], companyId)
                if (isEmpty(uploadResult)) {
                    throw new Error('Failed to upload new attachments')
                }
                newAttachments =
                    uploadResult?.map((upload) => ({
                        url: upload.url,
                        id: upload.fileId
                    })) || []
            }

            formattedAttachments = [...existingAttachments, ...newAttachments]
            break
        }

        default:
            throw new Error(`Invalid state: ${state}`)
    }

    const mappedKeys = {
        loan: 'loanId',
        invoice: 'invoiceId',
        receivables: 'receivableId'
    }

    const url = resolveApiUrl(`${key}.update`, {
        variables: {
            companyId,
            [mappedKeys[key]]: id
        }
    })

    const response = await requestService<T>(url, {
        method: 'PATCH',
        data: {
            attachments: formattedAttachments
        }
    })
    if (!response?.body) throw new Error('Invalid response from server')

    const mappedData = mapResponse ? await mapResponse(response.body) : response.body

    return {
        ...response,
        body: mappedData
    }
}
